import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  HierarchyNodesIcon,
  SubInfoIcon,
} from '../../../assets/icons';
import { Spinner } from '../../../components/ui';
import { useAppDispatch } from '../../../app/hooks';
import { territoryApi } from '../../../features/territory/territoryApi';
import {
  EC_LEVEL,
  ROOT,
  canHaveChildren,
  fetchConstituencyChildren,
  type Branch,
  type NodeKind,
  type TreeNode,
} from '../../../features/territory/constituencyTree';

export type { NodeKind };

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/* ------------------------------------------------------------------ *
 * Dynamic tree model. The tree keeps the sample structure/visuals, but
 * every level below the (static) root is fetched from the territory API
 * when its parent is expanded — passing the parent's code down:
 *
 *   National Central Committee (static)
 *     └─ State            GET /territory/state/
 *        └─ Parliament    GET /territory/parliament/?state=<state.id>
 *           └─ Assembly   GET /territory/assembly/?pc=<pc.id>
 *              ├─ Urban   GET /territory/block-ulb/?type=ULB
 *              │            └─ Ward  ?block_ulb_code → Village ?ward_code&type=ULB → Booth ?village_result_code
 *              └─ Rural   GET /territory/gram-panchayat/?ac_code__startswith=<ac_code>
 *                           └─ Village ?gram_panchayat_lgd_code&type=PRI → Booth ?village_result_code
 * ------------------------------------------------------------------ */

/** Reverse of EC_LEVEL: level rank → name (for prefilled saved areas not yet loaded). */
const LEVEL_NAME_BY_RANK: Record<number, string> = Object.values(EC_LEVEL).reduce(
  (acc, v) => {
    if (v) acc[v.rank] = v.name;
    return acc;
  },
  {} as Record<number, string>,
);

/** The EC area key (rank:code) used to match a node against a saved boundary. */
const nodeAreaKey = (node: TreeNode): string | null => {
  const lvl = EC_LEVEL[node.kind];
  return lvl && node.areaCode ? `${lvl.rank}:${node.areaCode}` : null;
};

/** A selected Assembly Constituency reported to the wizard (for step 4's Level 1). */
export interface AcOption {
  id: string;
  label: string;
}

/** A checked EC area reported to the wizard for the bulk scope POST on Confirm. */
export interface SelectedArea {
  levelName: string;
  levelRank: number;
  areaCode: string;
}

/** A previously-saved boundary (from GET) used to prefill the tree in edit/view mode. */
export interface SavedArea {
  levelRank: number;
  areaCode: string;
}

/**
 * A pruned, read-only view of the hierarchy reported to the wizard for step 3.
 * Contains every selected node plus its ancestors (the path) and, under a
 * selected node, its full loaded subtree.
 */
export interface HierarchyView {
  id: string;
  label: string;
  icon?: IconType;
  level?: string;
  sub?: string;
  /** Node kind + code — lets step 4 fetch the booths under a selected village leaf. */
  kind?: NodeKind;
  code?: string;
  /** Real EC area code (ps_code for booths) — matched against the org tree in step 4. */
  areaCode?: string;
  selected: boolean;
  children: HierarchyView[];
}

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

function Checkbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      className={[
        'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
        disabled
          ? 'cursor-not-allowed border border-[#e2e8f0] bg-[#f1f5f9]'
          : checked
            ? 'bg-brand text-white'
            : 'border border-[#cbd5e1] bg-white',
      ].join(' ')}
    >
      {checked && !disabled && <CheckmarkIcon className="size-4" />}
    </button>
  );
}

function Caret({
  hasChildren,
  expanded,
  onClick,
}: {
  hasChildren: boolean;
  expanded?: boolean;
  onClick?: () => void;
}) {
  if (!hasChildren) return <span className="size-4 shrink-0" />;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
      aria-label={expanded ? 'Collapse' : 'Expand'}
    >
      <CaretIcon className={['size-4 transition-transform', expanded ? '' : '-rotate-90'].join(' ')} />
    </button>
  );
}

function LevelBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
      {children}
    </span>
  );
}

function SelectedPill() {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2 rounded-full bg-[#10b981]" />
      <span className="text-[10px] font-bold tracking-[0.5px] text-[#059669]">SELECTED</span>
    </span>
  );
}

function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'flex h-[640px] flex-col overflow-hidden rounded-xl border border-line-soft bg-white shadow-topbar',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

const ROW = 'flex items-center gap-2.5 py-2';
const ROW_SELECTED =
  'flex items-center gap-2.5 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-3 py-2.5';

/* ------------------------------------------------------------------ *
 * Dynamic constituency tree
 * ------------------------------------------------------------------ */

function ConstituencyTree({
  active = true,
  search,
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
  onAreasChange,
  preselectedAreas,
}: {
  /** Load the state tree only when this step is active. */
  active?: boolean;
  /** Filters the top-level states via the state API's ?search= param. */
  search?: string;
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
  onAreasChange?: (areas: SelectedArea[]) => void;
  preselectedAreas?: SavedArea[];
}) {
  const dispatch = useAppDispatch();

  // Saved boundaries (edit/view) — auto-expanded + auto-checked in the tree.
  const savedAreas = useMemo(() => preselectedAreas ?? [], [preselectedAreas]);
  // Keyed by rank:code — used to prefill checks and to keep unloaded areas on save.
  const savedKeys = useMemo(
    () => new Set(savedAreas.map((a) => `${a.levelRank}:${a.areaCode}`)),
    [savedAreas],
  );

  const [expanded, setExpanded] = useState<Set<string>>(new Set(['root']));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [childrenMap, setChildrenMap] = useState<Record<string, TreeNode[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  // Flat lookup + loaded-children cache, kept in refs (updated as nodes load).
  const nodeMap = useRef<Record<string, TreeNode>>({ root: ROOT });
  const childrenRef = useRef<Record<string, TreeNode[]>>({});

  /** Fetch a node's children from the appropriate territory endpoint. */
  const fetchChildren = useCallback(
    (node: TreeNode) => fetchConstituencyChildren(dispatch, node, search),
    [dispatch, search],
  );

  // Fetch + cache a node's children, and auto-check any that match a saved boundary.
  const fetchAndStore = useCallback(
    async (node: TreeNode): Promise<TreeNode[]> => {
      const kids = await fetchChildren(node);
      kids.forEach((k) => {
        nodeMap.current[k.id] = k;
      });
      childrenRef.current[node.id] = kids;
      setChildrenMap((m) => ({ ...m, [node.id]: kids }));
      if (savedKeys.size > 0) {
        const toCheck = kids
          .filter((k) => {
            const key = nodeAreaKey(k);
            return key !== null && savedKeys.has(key);
          })
          .map((k) => k.id);
        if (toCheck.length > 0) {
          setSelected((s) => {
            const next = new Set(s);
            toCheck.forEach((id) => next.add(id));
            return next;
          });
        }
      }
      return kids;
    },
    [fetchChildren, savedKeys],
  );

  const loadChildren = useCallback(
    async (node: TreeNode) => {
      setLoadingIds((s) => new Set(s).add(node.id));
      setErrorIds((s) => {
        const n = new Set(s);
        n.delete(node.id);
        return n;
      });
      try {
        await fetchAndStore(node);
      } catch {
        setErrorIds((s) => new Set(s).add(node.id));
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [fetchAndStore],
  );

  // Like loadChildren, but returns the children (for the auto-expand driver).
  const ensureChildren = useCallback(
    async (node: TreeNode): Promise<TreeNode[]> => {
      if (childrenRef.current[node.id]) return childrenRef.current[node.id];
      setLoadingIds((s) => new Set(s).add(node.id));
      try {
        return await fetchAndStore(node);
      } finally {
        setLoadingIds((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [fetchAndStore],
  );

  // Resolve a saved area to a top-down list of node matchers (edit-mode reveal).
  const resolvePath = useCallback(
    async (area: SavedArea): Promise<((n: TreeNode) => boolean)[] | null> => {
      const ep = territoryApi.endpoints;
      const run = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();
      const code = area.areaCode;

      const stateM = (id: number | string) => (n: TreeNode) =>
        n.kind === 'state' && n.code === String(id);
      const pcM = (id: number | string) => (n: TreeNode) =>
        n.kind === 'parliament' && n.code === String(id);
      const acM = (c: string) => (n: TreeNode) => n.kind === 'assembly' && n.code === c;
      const branchM = (b: Branch) => (n: TreeNode) => n.kind === 'branch' && n.branch === b;
      const gramM = (c: string) => (n: TreeNode) => n.kind === 'gram' && n.code === c;
      const villageM = (c: string) => (n: TreeNode) => n.kind === 'village' && n.code === c;

      // ac_code → { stateId, pcId } via assembly then parliament retrieve.
      const acToStatePc = async (acCode: string) => {
        try {
          const asm = await run(
            dispatch(ep.getAssemblyByAc.initiate({ acCode }, { subscribe: false })),
          );
          const pcId = asm?.[0]?.pc;
          if (pcId == null) return null;
          const pc = await run(
            dispatch(ep.getParliamentById.initiate({ id: pcId }, { subscribe: false })),
          );
          if (pc?.state == null) return null;
          return { stateId: pc.state, pcId };
        } catch {
          return null;
        }
      };

      try {
        if (area.levelRank === 1) {
          return [(n) => n.kind === 'state' && n.areaCode === code];
        }
        if (area.levelRank === 2) {
          const pcs = await run(
            dispatch(ep.getParliamentByPcCode.initiate({ pcCode: code }, { subscribe: false })),
          );
          const p = pcs?.[0];
          if (!p?.state) return null;
          return [stateM(p.state), pcM(p.id)];
        }
        if (area.levelRank === 4) {
          const c = await acToStatePc(code);
          if (!c) return null;
          return [stateM(c.stateId), pcM(c.pcId), acM(code)];
        }
        if (area.levelRank === 6) {
          const gp = await run(
            dispatch(ep.getGramByLgd.initiate({ lgd: code }, { subscribe: false })),
          );
          const acCode = gp?.[0]?.ac_code;
          if (!acCode) return null;
          const c = await acToStatePc(acCode);
          if (!c) return null;
          return [stateM(c.stateId), pcM(c.pcId), acM(acCode), branchM('rural'), gramM(code)];
        }
        if (area.levelRank === 7) {
          // Find the village's assembly via a booth (ps_code encodes ac_code).
          const psv = await run(
            dispatch(ep.getPsVillageByResult.initiate({ resultCode: code }, { subscribe: false })),
          );
          const psCode = psv?.[0]?.ps_code;
          if (!psCode) return null;
          const acCode = psCode.split('_').slice(0, 3).join('_');
          const c = await acToStatePc(acCode);
          if (!c) return null;
          const rural = await run(
            dispatch(ep.getVillagesByAc.initiate({ acCode, type: 'PRI' }, { subscribe: false })),
          );
          const v = rural.find((x) => String(x.village_result_code) === String(code));
          if (v?.gram_panchayat_lgd_code) {
            return [
              stateM(c.stateId),
              pcM(c.pcId),
              acM(acCode),
              branchM('rural'),
              gramM(v.gram_panchayat_lgd_code),
              villageM(code),
            ];
          }
          return null;
        }
        return null;
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  // Edit-mode auto-expand: walk each saved area's path, expanding + checking it.
  const revealedRef = useRef<Set<string>>(new Set());
  const reveal = useCallback(
    async (area: SavedArea) => {
      const matchers = await resolvePath(area);
      if (!matchers) return;
      let node: TreeNode = ROOT;
      for (let i = 0; i < matchers.length; i += 1) {
        const kids = await ensureChildren(node);
        const next = kids.find(matchers[i]);
        if (!next) return;
        const parentId = node.id;
        setExpanded((s) => (s.has(parentId) ? s : new Set(s).add(parentId)));
        if (i === matchers.length - 1) {
          const leafId = next.id;
          setSelected((s) => (s.has(leafId) ? s : new Set(s).add(leafId)));
        }
        node = next;
      }
    },
    [resolvePath, ensureChildren],
  );

  useEffect(() => {
    if (savedAreas.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      for (const area of savedAreas) {
        const key = `${area.levelRank}:${area.areaCode}`;
        if (revealedRef.current.has(key)) continue;
        revealedRef.current.add(key);
        try {
          await reveal(area);
        } catch {
          /* ignore — the summary chip still shows it */
        }
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [savedAreas, reveal]);

  // Load the states under the root once the step is active, and re-load them whenever
  // the search term changes (the state API filters via ?search=).
  useEffect(() => {
    if (!active) return;
    void loadChildren(ROOT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, search]);

  const toggleExpand = (node: TreeNode) => {
    const willOpen = !expanded.has(node.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (willOpen) next.add(node.id);
      else next.delete(node.id);
      return next;
    });
    if (willOpen && canHaveChildren(node) && !childrenMap[node.id] && !loadingIds.has(node.id)) {
      void loadChildren(node);
    }
  };

  const toggleSelect = (node: TreeNode) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      return next;
    });

  // Report selection / branches / constituencies up to the wizard.
  useEffect(() => {
    const map = nodeMap.current;

    const build = (node: TreeNode, under = false): HierarchyView | null => {
      const isSel = selected.has(node.id);
      const inBranch = under || isSel;
      const kids = (childrenMap[node.id] || [])
        .map((c) => build(c, inBranch))
        .filter((x): x is HierarchyView => x !== null);
      if (inBranch || kids.length > 0) {
        return {
          id: node.id,
          label: node.label,
          icon: node.icon,
          level: node.level,
          sub: node.sub,
          kind: node.kind,
          code: node.code,
          areaCode: node.areaCode,
          selected: isSel,
          children: kids,
        };
      }
      return null;
    };

    const rootView = build(ROOT);
    onSelectionChange?.(rootView ? [rootView] : []);

    if (onBranchesChange) {
      const byAc: Record<string, Branch> = {};
      selected.forEach((id) => {
        const n = map[id];
        if (n?.branch && n.acId) byAc[n.acId] = n.branch;
      });
      onBranchesChange(byAc);
    }

    if (onConstituenciesChange) {
      const acs: AcOption[] = [];
      const seen = new Set<string>();
      selected.forEach((id) => {
        const n = map[id];
        if (n?.acId && !seen.has(n.acId)) {
          seen.add(n.acId);
          acs.push({ id: n.acId, label: n.acLabel || n.acId });
        }
      });
      onConstituenciesChange(acs);
    }

    if (onAreasChange) {
      const areas: SelectedArea[] = [];
      const emitted = new Set<string>();
      // Every checked node that maps to an EC level + has a real area code.
      selected.forEach((id) => {
        let n = map[id];
        // A Rural/Urban branch isn't an EC area itself — record its parent assembly instead.
        if (n?.kind === 'branch' && n.acId) n = map[n.acId] ?? n;
        const lvl = n && EC_LEVEL[n.kind];
        if (!n || !lvl || !n.areaCode) return;
        const key = `${lvl.rank}:${n.areaCode}`;
        if (emitted.has(key)) return;
        areas.push({ levelName: lvl.name, levelRank: lvl.rank, areaCode: n.areaCode });
        emitted.add(key);
      });
      // Keep saved boundaries whose node hasn't loaded yet (so nothing is lost on
      // re-confirm). Once a saved node loads it's auto-checked → emitted above; if the
      // user then unchecks it, its node exists but isn't selected → correctly dropped.
      const loadedKeys = new Set<string>();
      Object.values(map).forEach((n) => {
        const key = nodeAreaKey(n);
        if (key) loadedKeys.add(key);
      });
      savedAreas.forEach((a) => {
        const key = `${a.levelRank}:${a.areaCode}`;
        if (!loadedKeys.has(key) && !emitted.has(key)) {
          areas.push({
            levelName: LEVEL_NAME_BY_RANK[a.levelRank] ?? '',
            levelRank: a.levelRank,
            areaCode: a.areaCode,
          });
          emitted.add(key);
        }
      });
      onAreasChange(areas);
    }
  }, [
    selected,
    childrenMap,
    savedAreas,
    onSelectionChange,
    onBranchesChange,
    onConstituenciesChange,
    onAreasChange,
  ]);

  // Which settlement branch (urban/rural) is chosen per AC — the other is disabled.
  const chosenBranch: Record<string, Branch> = {};
  selected.forEach((id) => {
    const n = nodeMap.current[id];
    if (n?.branch && n.acId) chosenBranch[n.acId] = n.branch;
  });
  const isDisabled = (node: TreeNode) =>
    Boolean(
      node.branch && node.acId && chosenBranch[node.acId] && chosenBranch[node.acId] !== node.branch,
    );

  const renderNode = (node: TreeNode) => {
    const expandable = canHaveChildren(node);
    const open = expanded.has(node.id);
    const isSel = selected.has(node.id);
    const disabled = isDisabled(node);
    const kids = childrenMap[node.id];
    const isLoading = loadingIds.has(node.id);
    const hasError = errorIds.has(node.id);

    return (
      <div key={node.id}>
        <div className={[isSel ? ROW_SELECTED : ROW, disabled ? 'opacity-60' : ''].join(' ')}>
          <Caret hasChildren={expandable} expanded={open} onClick={() => toggleExpand(node)} />
          <Checkbox checked={isSel} disabled={disabled} onChange={() => toggleSelect(node)} />
          {node.icon ? (
            <node.icon className="size-5 shrink-0 text-brand" />
          ) : (
            <span className="flex size-5 shrink-0 items-center justify-center">
              <span className="size-1.5 rounded-full bg-[#94a3b8]" />
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (expandable) toggleExpand(node);
              else if (!disabled) toggleSelect(node);
            }}
            className={[
              'text-left text-sm',
              disabled
                ? 'text-[#94a3b8]'
                : isSel
                  ? 'font-semibold text-brand'
                  : 'font-medium text-ink',
            ].join(' ')}
          >
            {node.label}
          </button>
          {node.level ? (
            <span className="ml-auto">
              <LevelBadge>{node.level}</LevelBadge>
            </span>
          ) : (
            isSel && (
              <span className="ml-auto">
                <SelectedPill />
              </span>
            )
          )}
        </div>

        {node.sub && (
          <div className="flex items-center gap-1.5 pl-8 pt-1 text-xs text-[#94a3b8]">
            <SubInfoIcon className="h-[9px] w-[10px]" />
            {node.sub}
          </div>
        )}

        {expandable && open && (
          <div className="ml-[19px] flex flex-col gap-1.5 border-l border-dashed border-[#cbd5e1] pl-3">
            {node.groupLabel && (
              <p className="py-1 pl-6 text-xs font-medium text-[#94a3b8]">{node.groupLabel}</p>
            )}
            {isLoading && (
              <div className="flex items-center gap-2 py-2 pl-6 text-xs text-[#94a3b8]">
                <Spinner size={14} /> Loading…
              </div>
            )}
            {hasError && (
              <div className="flex items-center gap-2 py-2 pl-6 text-xs text-red-500">
                Failed to load.
                <button
                  type="button"
                  onClick={() => void loadChildren(node)}
                  className="font-semibold underline"
                >
                  Retry
                </button>
              </div>
            )}
            {!isLoading && !hasError && kids && kids.length === 0 && (
              <p className="py-2 pl-6 text-xs text-[#94a3b8]">No items found.</p>
            )}
            {!isLoading && !hasError && kids?.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1">{renderNode(ROOT)}</div>;
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

/** Step 2 — Define Operational Boundary (state → assembly → settlement → booth, from the API). */
export default function DefineBoundaryStep({
  active,
  onSelectionChange,
  onBranchesChange,
  onConstituenciesChange,
  onAreasChange,
  preselectedAreas,
}: {
  /** Load the state tree only when this step is active. */
  active?: boolean;
  onSelectionChange?: (nodes: HierarchyView[]) => void;
  onBranchesChange?: (branches: Record<string, Branch>) => void;
  onConstituenciesChange?: (acs: AcOption[]) => void;
  onAreasChange?: (areas: SelectedArea[]) => void;
  preselectedAreas?: SavedArea[];
}) {
  // Search box filters the top-level states via the state API's ?search= param (debounced).
  const [query, setQuery] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setStateSearch(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="flex flex-col gap-8">
      {/* Search */}
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-[#94a3b8]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search state"
          className="w-full rounded-lg border border-line-soft bg-white py-3.5 pl-[41px] pr-3.5 text-sm text-ink shadow-card placeholder:text-[#94a3b8] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
      </div>

      {/* Full hierarchy selector */}
      <Card>
        <div className="flex items-center justify-between border-b border-[#f1f5f9] px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-md bg-[#eff6ff] text-brand">
              <HierarchyNodesIcon className="size-5" />
            </span>
            <h2 className="text-base font-semibold text-ink">Select Constituency Node</h2>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 py-6">
          <ConstituencyTree
            active={active}
            search={stateSearch}
            onSelectionChange={onSelectionChange}
            onBranchesChange={onBranchesChange}
            onConstituenciesChange={onConstituenciesChange}
            onAreasChange={onAreasChange}
            preselectedAreas={preselectedAreas}
          />
        </div>
      </Card>
    </div>
  );
}
